import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../context/AuthContext';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setValidating(false);
      return;
    }

    try {
      await axios.get(`${API}/auth/validate-reset-token?token=${token}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Wagwoorde stem nie ooreen nie');
      return;
    }

    if (password.length < 6) {
      toast.error('Wagwoord moet ten minste 6 karakters wees');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Wagwoord suksesvol herstel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie wagwoord herstel nie');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen py-24 md:py-32 px-6 md:px-12 flex items-center justify-center">
        <p className="font-body text-xl text-muted-charcoal">Besig om te verifieer...</p>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen py-24 md:py-32 px-6 md:px-12 flex items-center justify-center">
        <div className="w-full max-w-md text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-charcoal mb-8">
            Ongeldige Skakel
          </h1>
          <div className="bg-parchment border border-[#E5E0D6] p-8 md:p-12">
            <p className="font-body text-lg text-muted-charcoal mb-6">
              Hierdie herstel-skakel is ongeldig of het verval.
            </p>
            <Link 
              to="/forgot-password" 
              className="text-gold hover:underline font-ui text-sm uppercase tracking-widest"
            >
              Versoek 'n nuwe skakel
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen py-24 md:py-32 px-6 md:px-12 flex items-center justify-center">
        <div className="w-full max-w-md text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-charcoal mb-8">
            Wagwoord Herstel
          </h1>
          <div className="bg-parchment border border-[#E5E0D6] p-8 md:p-12">
            <p className="font-body text-lg text-muted-charcoal mb-6">
              U wagwoord is suksesvol herstel. U kan nou aanmeld met u nuwe wagwoord.
            </p>
            <Link 
              to="/login" 
              className="inline-block bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-8 py-4 font-ui uppercase tracking-widest text-sm transition-colors duration-300"
            >
              Teken Aan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32 px-6 md:px-12 flex items-center justify-center">
      <div className="w-full max-w-md">
        <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-charcoal mb-8 text-center">
          Nuwe Wagwoord
        </h1>

        <div className="bg-parchment border border-[#E5E0D6] p-8 md:p-12">
          <p className="font-body text-lg text-muted-charcoal mb-6">
            Kies 'n nuwe wagwoord vir u rekening.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-ui text-sm uppercase tracking-widest text-charcoal mb-2">
                Nuwe Wagwoord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="reset-password-new"
                className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold px-0 py-4 text-xl font-body placeholder:text-charcoal/40 outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block font-ui text-sm uppercase tracking-widest text-charcoal mb-2">
                Bevestig Wagwoord
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="reset-password-confirm"
                className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold px-0 py-4 text-xl font-body placeholder:text-charcoal/40 outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit"
              className="w-full bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-8 py-4 font-ui uppercase tracking-widest text-sm transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? 'Besig...' : 'Herstel Wagwoord'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
